import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Calendar } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { api, mediaUrl } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const CATS = [
  { slug: "", label: "Tutti" },
  { slug: "amministrazioni", label: "Amministrazioni condominiali" },
  { slug: "immobiliare", label: "Immobiliare" },
  { slug: "ristrutturazioni", label: "Ristrutturazioni" },
  { slug: "affitto", label: "Affitto" },
];
const catLabel = (s) => (CATS.find((c) => c.slug === s)?.label) || "Immobiliare";
const fallback = (c) => `/img/blog/${c || "immobiliare"}-1.jpg`;

export default function Blog() {
  useSeo({
    title: "Blog Immobiliare e Condominiale | Scandicci e Firenze | Edilstudio",
    description: "Guide su compravendita, ristrutturazioni, bonus fiscali, gestione condominiale e affitti a Scandicci e Firenze.",
    canonicalPath: "/blog",
  });
  const [cat, setCat] = useState("");
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["posts"], queryFn: async () => (await api.get("/posts")).data });
  const filtered = cat ? posts.filter((p) => p.category === cat) : posts;

  return (
    <PublicLayout>
      <div className="bg-[#1B2A41] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#F5A623]">Blog</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mt-2">Guide, mercato e consigli</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Approfondimenti su immobiliare, ristrutturazioni, affitti e gestione condominiale nel territorio di Scandicci e Firenze.</p>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        {/* Category navigation */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="blog-categories">
          {CATS.map((c) => (
            <button key={c.slug} onClick={() => setCat(c.slug)} data-testid={`blog-cat-${c.slug || "all"}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${cat === c.slug ? "bg-[#2472C8] text-white border-[#2472C8]" : "bg-white text-[#566476] border-[#DCE6EF] hover:border-[#2472C8]/40"}`}>
              {c.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="py-20 text-center font-mono text-[#8695A6]">Caricamento articoli…</div>
        ) : filtered.length ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="bg-white rounded-2xl overflow-hidden border border-[#DCE6EF] hover:shadow-xl transition-shadow group" data-testid={`blog-card-${p.id}`}>
                <div className="relative aspect-video overflow-hidden">
                  <img src={mediaUrl(p.cover_image) || fallback(p.category)} onError={(e) => { e.currentTarget.src = fallback(p.category); }} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <span className="absolute top-3 left-3 bg-[#1B2A41]/85 backdrop-blur text-white text-[11px] font-semibold px-3 py-1 rounded-full">{catLabel(p.category)}</span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-1.5 text-xs font-mono text-[#2472C8]"><Calendar className="w-3.5 h-3.5" /> {new Date(p.created_at).toLocaleDateString("it-IT")}</div>
                  <h3 className="font-heading font-semibold text-[#1B2A41] text-lg mt-2 group-hover:text-[#2472C8] transition-colors">{p.title}</h3>
                  <p className="text-sm text-[#566476] mt-2 line-clamp-3">{p.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-[#2472C8] font-semibold text-sm mt-4 group-hover:gap-2 transition-all">Leggi <ArrowRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="py-20 bg-white rounded-2xl border border-dashed border-[#CBD7E2] text-center text-[#8695A6]" data-testid="blog-empty">Nessun articolo in questa categoria.</div>
        )}
      </section>
    </PublicLayout>
  );
}
